// Relatórios do mês e Despesas e lucro. Os números vêm dos livros (o que entrou de verdade).
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { api } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useWide } from '../app/desk.tsx';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { can, useMe } from '../lib/data.ts';
import { DOW_LONG, money, moneyShort, MONTHS, nowLocal, parseMoney } from '../lib/format.ts';
import { MONTHS_SHORT, shiftMonth, useExpenses, useReport, type MonthReport } from '../lib/store.ts';
import { BackHeader } from '../ui/Header.tsx';
import { Failed, Skeleton } from '../ui/State.tsx';
import { Icon } from '../ui/Icon.tsx';
import { Sheet } from '../ui/Sheet.tsx';

const monthName = (m: string) => MONTHS[Number(m.slice(5, 7)) - 1]!;
const cum = (a: number[]) => { let s = 0; return a.map((v) => (s += v)); };

function MonthNav({ month, set }: { month: string; set: (m: string) => void }) {
  const cur = nowLocal().date.slice(0, 7);
  return (
    <div className="week-nav"><button aria-label="Mês anterior" onClick={() => set(shiftMonth(month, -1))}><Icon name="back" /></button>
      <span>{monthName(month)} de {month.slice(0, 4)}</span>
      <button aria-label="Próximo mês" disabled={month >= cur} onClick={() => set(shiftMonth(month, 1))}><Icon name="chev" /></button></div>
  );
}

function Bars({ items }: { items: Array<{ label: string; sub: string; amount: number }> }) {
  const max = Math.max(1, ...items.map((i) => i.amount));
  return <div className="cx-bars">{items.map((i) => (
    <div key={i.label} className="cx-bar"><div className="cx-bar-top"><span>{i.label}<small>{i.sub}</small></span><b>{money(i.amount)}</b></div>
      <i style={{ '--w': `${Math.max(2, Math.round((i.amount / max) * 100))}%` } as React.CSSProperties} /></div>
  ))}</div>;
}

// acumulado do mês contra o mês anterior, com a linha "no ritmo" até o fim do mês
function Chart({ r, wide = false }: { r: MonthReport; wide?: boolean }) {
  const [hover, setHover] = useState<number | null>(null);
  const ca = cum(r.daily), cp = cum(r.prevDaily);
  const days = r.daily.length, upTo = r.today ?? days;
  const W = wide ? 640 : 340, H = wide ? 220 : 150, P = { l: 4, r: 4, t: 14, b: 22 };
  const max = Math.max(r.paceCents ?? 0, cp.at(-1) ?? 0, ca[upTo - 1] ?? 0, 1) * 1.05;
  const x = (d: number) => P.l + ((d - 1) / Math.max(days - 1, 1)) * (W - P.l - P.r);
  const y = (v: number) => P.t + (1 - v / max) * (H - P.t - P.b);
  const path = (a: number[]) => a.map((v, i) => `${i ? 'L' : 'M'}${x(i + 1).toFixed(1)} ${y(v).toFixed(1)}`).join(' ');
  const ex = x(upTo), ey = y(ca[upTo - 1] ?? 0);
  const mm = r.month.slice(5);
  return (
    <div className="rc-wrap" onPointerLeave={() => setHover(null)}>
      <svg viewBox={`0 0 ${W} ${H}`} className="rc" role="img" aria-label={`Recebido acumulado: ${monthName(r.month)} ${moneyShort(ca[upTo - 1] ?? 0)}, ${monthName(r.prevMonth)} ${moneyShort(r.prevSameDayCents)} no mesmo dia`}>
        {[0.5, 1].map((f) => <line key={f} x1={P.l} x2={W - P.r} y1={y(max * f)} y2={y(max * f)} className="rc-grid" />)}
        <line x1={P.l} x2={W - P.r} y1={y(0)} y2={y(0)} className="rc-base" />
        <path d={path(cp.slice(0, days))} className="rc-jul" />
        {r.paceCents != null && <path d={`M${ex} ${ey} L${x(days)} ${y(r.paceCents)}`} className="rc-pace" />}
        <path d={path(ca.slice(0, upTo))} className="rc-aug" />
        <circle cx={ex} cy={ey} r={4.5} className="rc-dot" />
        {(wide ? [1, 5, 10, 15, 20, 25, days] : [1, 10, 20, days]).map((d) => <text key={d} x={x(d)} y={H - 6} textAnchor={d === 1 ? 'start' : d === days ? 'end' : 'middle'} className="rc-tick">{d}/{mm}</text>)}
        {hover && <line className="rc-cross" x1={x(hover)} x2={x(hover)} y1={P.t} y2={y(0)} style={{ opacity: 1 }} />}
        {Array.from({ length: days }, (_, i) => <rect key={i} className="rc-hit" x={x(i + 1) - 5.5} y={0} width={11} height={H} onPointerEnter={() => setHover(i + 1)} />)}
      </svg>
      {hover && <div className="rc-tip" style={{ left: `${Math.max(18, Math.min(82, (x(hover) / W) * 100))}%` }}>
        <b>{hover}/{mm}</b>{hover <= upTo && <span><i className="aug" />{monthName(r.month)} {moneyShort(ca[hover - 1] ?? 0)}</span>}
        <span><i className="jul" />{monthName(r.prevMonth)} {moneyShort(cp[hover - 1] ?? cp.at(-1) ?? 0)}</span></div>}
    </div>
  );
}

// variação contra o mês anterior (no mesmo dia, quando é o mês corrente). "melhor" diz se subir é bom.
function Delta({ cur, prev, better = 'up', pts = false }: { cur: number | null; prev: number | null; better?: 'up' | 'down'; pts?: boolean }) {
  if (cur == null || prev == null || (!pts && prev === 0)) return <span className="kpi-d none">sem base para comparar</span>;
  const d = pts ? cur - prev : (cur - prev) / prev;
  const n = pts ? Math.round(d) : Math.round(d * 100);
  if (n === 0) return <span className="kpi-d flat">igual ao mês anterior</span>;
  const good = (n > 0) === (better === 'up');
  return <span className={'kpi-d ' + (good ? 'good' : 'bad')}>{n > 0 ? '▲' : '▼'} {Math.abs(n)}{pts ? ' p.p.' : '%'}</span>;
}

// mês corrente: o que já foi feito, o que está marcado e onde o mês deve fechar
function Forecast({ r }: { r: MonthReport }) {
  const f = r.forecast!;
  const scale = Math.max(f.projectionCents, f.billedCents + f.bookedCents, 1);
  const pct = (v: number) => `${Math.min(100, (v / scale) * 100)}%`;
  const lastDay = r.daily.length;
  const why = f.basis === 'agenda'
    ? `Faturado + o que está marcado${f.lossRate > 0 ? `, menos ${Math.round(f.lossRate * 100)}% (a média de faltas e cancelamentos dos últimos 3 meses)` : ''}.`
    : `A agenda dos ${f.openDaysLeft} dias de salão aberto que faltam ainda está mais vazia que o normal. Para eles, a conta usa a média de ${moneyShort(f.dailyAvgCents)} por dia dos últimos 3 meses.`;
  return (
    <section className="fx" aria-label={`Faturamento de ${monthName(r.month)}`}>
      <header><b>Faturamento de {monthName(r.month)}</b><small>valor dos serviços feitos e marcados no mês</small></header>
      <p className="m-hint" style={{ margin: '0 0 10px' }}>Faturado é o valor dos serviços já feitos. Recebido é o dinheiro que realmente entrou (Pix, cartão, dinheiro). Os dois podem ser diferentes: pagar depois, desconto, sinal adiantado, vale e pacote.</p>
      <div className="fx-nums">
        <div><span><i className="k done" />Faturado até agora</span><strong>{moneyShort(f.billedCents)}</strong><small>{r.done} {r.done === 1 ? 'atendimento feito' : 'atendimentos feitos'}</small></div>
        <div><span><i className="k booked" />Previsto na agenda</span><strong>{moneyShort(f.bookedCents)}</strong><small>{f.bookedCount} {f.bookedCount === 1 ? 'horário marcado' : 'horários marcados'} até o dia {lastDay}</small></div>
        <div className="proj"><span><i className="k proj" />Projeção para o fim do mês</span><strong>{moneyShort(f.projectionCents)}</strong><small>{f.openDaysLeft} {f.openDaysLeft === 1 ? 'dia aberto pela frente' : 'dias abertos pela frente'}</small></div>
      </div>
      <div className="fx-bar" role="img" aria-label={`Faturado ${moneyShort(f.billedCents)}, marcado ${moneyShort(f.bookedCents)}, projeção ${moneyShort(f.projectionCents)}`}>
        <i className="done" style={{ width: pct(f.billedCents) }} />
        <i className="booked" style={{ width: pct(f.bookedCents) }} />
        <b style={{ left: pct(f.projectionCents) }} />
      </div>
      <p className="fx-why">{why}</p>
    </section>
  );
}

const PAY: Record<string, string> = { pix: 'Pix', credit: 'Crédito', debit: 'Débito', cash: 'Dinheiro', client_credit: 'Crédito de cliente' };

export function Relatorios() {
  const nav = useNav();
  const me = useMe().data!;
  const wide = useWide();
  const [month, setMonth] = useState(nowLocal().date.slice(0, 7));
  const q = useReport(month);
  let r = q.data;
  const head = (
    <div className="rel-top">
      <BackHeader title="Relatórios" />
      <MonthNav month={month} set={setMonth} />
    </div>
  );
  if (!r) return <div className="view-enter rel2">{head}<p className="m-hint rg-hint">{q.isLoading ? 'Calculando…' : msg(q.error)}</p></div>;
  // uma projeção só: no mês corrente vale a do faturamento (com a agenda), não o ritmo do que entrou
  if (r.forecast) r = { ...r, paceCents: null };
  const empty = r.receivedCents === 0 && r.done === 0;
  const open = r.byDow.filter((d) => d.avgCents > 0);
  const top = open.slice().sort((a, b) => b.avgCents - a.avgCents)[0], low = open.slice().sort((a, b) => a.avgCents - b.avgCents)[0];
  const maxDow = Math.max(1, ...r.byDow.map((d) => d.avgCents));
  // domingo só aparece se o salão abriu em algum domingo do mês
  const dows = [1, 2, 3, 4, 5, 6, 0].filter((d) => d !== 0 || r.byDow[0]!.avgCents > 0);
  const period = r.today ? `até o dia ${r.today}` : 'mês inteiro';
  const vs = `vs ${MONTHS_SHORT[Number(r.prevMonth.slice(5)) - 1]}${r.today ? ' no mesmo dia' : ''}`;
  const noShowRate = r.done + r.noShows ? (r.noShows / (r.done + r.noShows)) * 100 : null;
  const prevNoShowRate = r.prev.done + r.prev.noShows ? (r.prev.noShows / (r.prev.done + r.prev.noShows)) * 100 : null;
  const payTotal = r.payMix.reduce((t, p) => t + p.amount, 0);
  const acts: Array<{ t: string; s: string; b: string; go: () => void }> = [];
  if (r.attention.away) acts.push({ t: `${r.attention.away} ${r.attention.away > 1 ? 'clientes sumiram' : 'cliente sumiu'}`, s: 'Vieram nos últimos 6 meses, mas não voltam há 45 dias e não têm horário.', b: 'Ver quem', go: () => nav.go('clientes', { filter: 'away' }) });
  if (r.attention.birthdays) acts.push({ t: `${r.attention.birthdays} ${r.attention.birthdays > 1 ? 'aniversariantes' : 'aniversariante'} em ${monthName(r.month)}`, s: 'Um mimo na semana do aniversário fideliza.', b: 'Ver quem', go: () => nav.go('clientes', { filter: 'birthday' }) });
  if (top && low && top.dow !== low.dow) acts.push({ t: `${DOW_LONG[low.dow]} é o dia mais fraco`, s: `Média de ${moneyShort(low.avgCents)} por dia, contra ${moneyShort(top.avgCents)} ${top.dow === 6 ? 'no sábado' : top.dow === 0 ? 'no domingo' : `na ${DOW_LONG[top.dow]!.toLowerCase()}`}.`, b: 'Chamar sumidas', go: () => nav.go('clientes', { filter: 'away' }) });
  const p = r.profit;
  const line = (l: string, v: number, kind: 'in' | 'out' | 'tot') => (
    <div className={'rl-line ' + kind}><span>{l}</span><b>{kind === 'out' ? '− ' : ''}{money(v)}</b>
      {kind === 'out' && p.receivedCents > 0 && <i style={{ '--w': `${Math.max(1, Math.round((v / p.receivedCents) * 100))}%` } as React.CSSProperties} />}</div>
  );
  return (
    <div className="view-enter rel2">
      {head}
      {r.forecast && <Forecast r={r} />}
      {empty ? <div className="empty-box"><b>Nada recebido em {monthName(r.month)}</b><p>O relatório se monta com as cobranças: recebido, atendimentos, ticket médio e cada parceira, comparados com o mês anterior.</p></div> : <>
        <div className="kpis" aria-label={`Resumo de ${monthName(r.month)}`}>
          <div className="kpi main"><span>Recebido · {period}{r.historical ? ' · Trinks' : ''}</span><strong>{moneyShort(r.receivedCents)}</strong><Delta cur={r.receivedCents} prev={r.prevSameDayCents} /><small>{vs}{r.paceCents != null ? ` · no ritmo, fecha em ${moneyShort(r.paceCents)}` : ''}</small></div>
          <div className="kpi"><span>Atendimentos</span><strong>{r.done}</strong><Delta cur={r.done} prev={r.prev.done} /><small>{vs}</small></div>
          <div className="kpi"><span>Ticket médio</span><strong>{r.ticketCents != null ? moneyShort(r.ticketCents) : '—'}</strong><Delta cur={r.ticketCents} prev={r.prev.ticketCents} /><small>valor dos serviços por atendimento</small></div>
          <div className="kpi"><span>Faltas</span><strong>{noShowRate != null ? `${Math.round(noShowRate)}%` : '—'}</strong><Delta cur={noShowRate} prev={prevNoShowRate} better="down" pts /><small>{r.noShows} {r.noShows === 1 ? 'falta' : 'faltas'} no mês</small></div>
          <div className="kpi"><span>Clientes novas</span><strong>{r.newClients}</strong><Delta cur={r.newClients} prev={r.prev.newClients} /><small>{r.returnRate != null ? `${r.returnRate}% das clientes voltam em 60 dias` : 'primeira visita no mês'}</small></div>
        </div>

        {acts.length > 0 && <div className="rel-insights">{acts.map((a) => (
          <div key={a.t} className="rel-ins"><Icon name="info" /><span><strong>{a.t}</strong><small>{a.s}</small></span><button onClick={a.go}>{a.b}<Icon name="chevR" /></button></div>
        ))}</div>}

        <div className="rel-grid">
          {r.historical ? (
            <section className="rel-card rel-chart rel-hist">
              <header><b>Mês do Trinks</b><small>sistema que o salão usava antes do app</small></header>
              <p>O Trinks informa o total do mês e as formas de pagamento. O gráfico dia a dia, as cotas das parceiras e as taxas começam a aparecer com as cobranças feitas no app.</p>
            </section>
          ) : (
          <section className="rel-card rel-chart">
            <header><b>Recebido dia a dia</b><small>acumulado de {monthName(r.month)} contra {monthName(r.prevMonth)}</small></header>
            <Chart r={r} wide={wide} />
            <div className="rc-legend"><span><i className="aug" />{monthName(r.month)}</span><span><i className="jul" />{monthName(r.prevMonth)}</span>{r.paceCents != null && <span><i className="pace" />No ritmo atual: {moneyShort(r.paceCents)}</span>}</div>
            {r.receivedCents === 0 && r.done > 0 && <p className="m-hint">Os atendimentos de antes do app (importados) entram nos números de serviço. O dinheiro recebido conta a partir das cobranças feitas no app.</p>}
          </section>
          )}

          {can(me, 'valoresSalao') && !r.historical && (
            <section className="rel-card rel-profit">
              <header><b>Lucro do salão</b><small>{period}</small></header>
              <strong className="rl-big">{money(p.profitCents)}</strong>
              <p className="rl-pct">{p.receivedCents ? `${Math.round((p.profitCents / p.receivedCents) * 100)}% do que entrou ficou com o salão` : ''}</p>
              <div className="rl-lines">
                {line('Recebido', p.receivedCents, 'in')}
                {line('Cotas das parceiras', p.cotasCents, 'out')}
                {line('Gorjetas (das parceiras)', p.tipsCents, 'out')}
                {line('Taxas de pagamento', p.feesCents, 'out')}
                {line('Despesas do salão', p.expensesCents, 'out')}
              </div>
              {p.prepaidCents > 0 && <p className="m-hint">Inclui {money(p.prepaidCents)} de vales e pacotes vendidos e ainda não usados.</p>}
              <button className="rl-more" onClick={() => nav.go('despesas', { month })}>Despesas e lançamentos<Icon name="chevR" /></button>
            </section>
          )}

          {r.byPro.some((x) => x.done || x.production || x.noShows) && <section className="rel-card rel-team">
            <header><b>Por parceira</b><small>serviços feitos em {monthName(r.month)}</small></header>
            <table className="rt">
              <thead><tr><th>Parceira</th><th className="num">Atend.</th><th className="num">Produção</th><th className="num">Ticket</th><th className="num">Faltas</th></tr></thead>
              <tbody>{r.byPro.filter((x) => x.done || x.production || x.noShows).map((x) => (
                <tr key={x.id}>
                  <td><b>{x.name}</b><i className="rt-share" style={{ '--w': `${r.productionCents ? Math.round((x.production / r.productionCents) * 100) : 0}%` } as React.CSSProperties} /></td>
                  <td className="num">{x.done}</td><td className="num">{moneyShort(x.production)}</td>
                  <td className="num">{x.done ? moneyShort(Math.round(x.production / x.done)) : '—'}</td><td className="num">{x.noShows}</td>
                </tr>
              ))}</tbody>
            </table>
          </section>}

          {payTotal > 0 && (
            <section className="rel-card rel-pay">
              <header><b>Como pagaram</b><small>dinheiro que entrou</small></header>
              <div className="pay-stack" role="img" aria-label={r.payMix.map((x) => `${PAY[x.method] ?? x.method} ${Math.round((x.amount / payTotal) * 100)}%`).join(', ')}>
                {r.payMix.map((x) => <i key={x.method} className={'pm-' + x.method} style={{ flexGrow: x.amount }} />)}
              </div>
              <div className="pay-list">{r.payMix.map((x) => (
                <div key={x.method} className="pay-row"><span><i className={'pm-' + x.method} />{PAY[x.method] ?? x.method}</span><em>{Math.round((x.amount / payTotal) * 100)}%</em><b>{moneyShort(x.amount)}</b>
                  {x.n > 0 && <small>{x.n} {x.n === 1 ? 'pagamento' : 'pagamentos'}{x.fees ? ` · ${money(x.fees)} de taxa` : ''}</small>}</div>
              ))}</div>
            </section>
          )}

          {r.byService.length > 0 && <section className="rel-card rel-svc"><header><b>Serviços que mais faturam</b><small>valor dos serviços feitos</small></header>
            <Bars items={r.byService.map((s) => ({ label: s.name, sub: `${s.n} ${s.n > 1 ? 'atendimentos' : 'atendimento'} · ${Math.round((s.value / Math.max(r.productionCents, 1)) * 100)}%`, amount: s.value }))} /></section>}

          {open.length > 1 && <section className="rel-card rel-dow"><header><b>Dias mais fortes</b><small>média recebida por dia aberto</small></header>
            <div className="rd-cols" style={{ gridTemplateColumns: `repeat(${dows.length}, 1fr)` }}>{dows.map((d) => { const v = r.byDow[d]!.avgCents; return (
              <div key={d} className={'rd-col' + (top && d === top.dow ? ' top' : '')} title={`${DOW_LONG[d]}: média de ${moneyShort(v)} por dia`}><b>{v ? moneyShort(v) : '—'}</b><i style={{ height: Math.max(6, Math.round((v / maxDow) * 96)) }} /><span>{DOW_LONG[d]!.slice(0, 3)}</span></div>
            ); })}</div></section>}
        </div>
      </>}
      <div style={{ height: 24 }} />
    </div>
  );
}

// ---------- despesas ----------
export function Despesas() {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const [month, setMonth] = useState(nav.params.month ?? nowLocal().date.slice(0, 7));
  const ex = useExpenses(month);
  const rep = useReport(month);
  const [voiding, setVoiding] = useState<string | null>(null);
  const items = ex.data?.items ?? [];
  const live = items.filter((e) => !e.voidedAt);
  const p = rep.data?.profit;
  const byCat = new Map<string, number>();
  for (const e of live) byCat.set(e.category, (byCat.get(e.category) ?? 0) + e.amountCents);
  const total = live.reduce((t, e) => t + e.amountCents, 0);
  const tirar = async (id: string, reason: string) => {
    try { await api.post(`/expenses/${id}/void`, { reason }); setVoiding(null); await Promise.all(['expenses', 'report'].map((k) => qc.invalidateQueries({ queryKey: [k] }))); toast('Despesa retirada. Ela continua no histórico.'); } catch (e) { toast(msg(e), 'warn'); }
  };
  const line = (l: string, v: number, neg = false, strong = false) => <div className={'lc-line' + (strong ? ' tot' : '')}><span>{l}</span><b>{neg ? '− ' : ''}{money(v)}</b></div>;
  return (
    <div className="view-enter cp2"><BackHeader title="Despesas e lucro" />
      <MonthNav month={month} set={setMonth} />
      <div className="cp2-body">
        {p && <div className="lucro-card"><span>{monthName(month)}</span>
          {line('Recebido', p.receivedCents)}{line('Gorjetas (das parceiras)', p.tipsCents, true)}{line('Cotas das parceiras', p.cotasCents, true)}{line('Taxas de pagamento', p.feesCents, true)}{line('Despesas do salão', p.expensesCents, true)}
          {line('Lucro do salão', p.profitCents, false, true)}
          <small>{p.receivedCents ? `${Math.round((p.profitCents / p.receivedCents) * 100)}% do recebido` : ''}{p.prepaidCents ? ` · inclui ${money(p.prepaidCents)} de vales e pacotes ainda a realizar` : ''}</small></div>}
        {byCat.size > 0 && <div className="hsec"><div className="hsec-label"><b>Despesas por categoria</b></div><div className="card rel-card">
          <Bars items={[...byCat].sort((a, b) => b[1] - a[1]).map(([k, v]) => ({ label: k, sub: `${Math.round((v / Math.max(total, 1)) * 100)}% das despesas`, amount: v }))} /></div></div>}
        <p className="gl-title">Lançamentos</p>
        <div className="gl">
          {ex.data === undefined ? (ex.isError ? <Failed compact error={ex.error} retry={() => ex.refetch()} /> : <Skeleton rows={3} />) : !items.length && <p className="m-hint rg-hint" style={{ marginTop: 12 }}>Nenhuma despesa neste mês. Comece pelo aluguel e pelas contas fixas.</p>}
          {items.map((e) => (
            <div key={e.id}>
              <div className={'gl-row bq-row dp-row' + (e.voidedAt ? ' voided' : '')}><span>{e.date.slice(8)}/{e.date.slice(5, 7)}</span>
                <b>{e.description || e.category}<small>{e.category}{e.recurring || e.recurringOf ? ' · todo mês' : ''}{e.cashMovementId ? ' · saiu da gaveta' : ''}{e.voidedAt ? ` · retirada: ${e.voidReason}` : ''}</small></b>
                <em className="sv-price">{money(e.amountCents)}</em>
                {!e.voidedAt && !e.cashMovementId && <button className="gl-act" onClick={() => setVoiding(voiding === e.id ? null : e.id)} aria-label={`Tirar ${e.description || e.category}`}>Tirar</button>}</div>
              {voiding === e.id && <div className="gl-row"><span>Por quê?</span><span className="row-acts">{['Lançada errada', 'Lançada duas vezes', 'Não vai mais pagar'].map((r) => <button key={r} className="gl-act" onClick={() => tirar(e.id, r)}>{r}</button>)}</span></div>}
            </div>
          ))}
          <button className="gl-row gl-btn ac-invite" onClick={() => nav.open({ kind: 'despesa', month })}><b>+ Lançar despesa</b></button>
        </div>
        <p className="m-hint">Despesa lançada não se apaga: "Tirar" deixa registrado quem tirou e por quê. A que repete todo mês para de repetir.</p>
      </div>
      <div style={{ height: 24 }} />
    </div>
  );
}

export function DespesaSheet({ month }: { month?: string }) {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const today = nowLocal().date;
  const [valor, setValor] = useState('');
  const [cat, setCat] = useState('');
  const [desc, setDesc] = useState('');
  const [rec, setRec] = useState(false);
  const [gaveta, setGaveta] = useState(false);
  const [busy, setBusy] = useState(false);
  const cats = useExpenses(month ?? today.slice(0, 7)).data?.categories ?? [];
  const cents = parseMoney(valor);
  const date = month && month !== today.slice(0, 7) ? `${month}-01` : today;
  const miss = !(cents > 0) ? 'Digite o valor' : !cat ? 'Escolha a categoria' : '';
  async function save() {
    if (miss) return;
    setBusy(true);
    try {
      await api.post('/expenses', { date, category: cat, description: desc.trim(), amountCents: cents, recurring: rec, fromCash: gaveta });
      await Promise.all(['expenses', 'report', 'cash'].map((k) => qc.invalidateQueries({ queryKey: [k] })));
      toast(`Despesa de ${money(cents)} lançada.${gaveta ? ' Saiu da gaveta como sangria.' : ''}`);
      nav.close();
    } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); }
  }
  return (
    <Sheet title="Nova despesa" sub={date === today ? 'hoje' : `dia 01/${date.slice(5, 7)}`} actions={<><button className="cta" disabled={!!miss || busy} onClick={save}>{busy ? 'Lançando…' : miss || 'Lançar despesa'}</button><button className="link-back" onClick={nav.close}>Cancelar</button></>}>
      <div className="field"><label htmlFor="dpValor">Valor</label><div className="money-in"><span>R$</span><input id="dpValor" inputMode="decimal" autoComplete="off" placeholder="0,00" value={valor} onChange={(e) => setValor(e.target.value)} autoFocus /></div></div>
      <p className="gl-title">Categoria</p>
      <div className="chipset soft">{cats.map((c) => <button key={c} className={cat === c ? 'on' : ''} aria-pressed={cat === c} onClick={() => setCat(c)}>{c}</button>)}</div>
      <div className="field"><label htmlFor="dpDesc">Descrição <span>opcional</span></label><input className="text-in" id="dpDesc" autoComplete="off" placeholder="Ex.: acetona e algodão" value={desc} onChange={(e) => setDesc(e.target.value)} /></div>
      <div className="gl" style={{ marginTop: 12 }}>
        <div className="gl-row toggle-line"><span><strong>Repete todo mês</strong></span><button role="switch" aria-checked={rec} aria-label="Repete todo mês" onClick={() => setRec(!rec)}><Icon name={rec ? 'toggleOn' : 'toggleOff'} /></button></div>
        {date === today && <div className="gl-row toggle-line"><span><strong>Saiu da gaveta de dinheiro</strong><small>vira uma sangria no caixa de hoje</small></span><button role="switch" aria-checked={gaveta} aria-label="Saiu da gaveta de dinheiro" onClick={() => setGaveta(!gaveta)}><Icon name={gaveta ? 'toggleOn' : 'toggleOff'} /></button></div>}
      </div>
    </Sheet>
  );
}
